/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import i18n from 'core/common/i18n'
import { Box, Grid, CircularProgress } from 'core/components/Core'
import Typography from 'core/components/Typography'
import { actionsUpload, useUpload } from '../../context'
import FileActions from '../FileActions'
import FileError from '../FileError'
import { CircularContainer, FirstMsg, Preview, Thumbnail } from './style'

const type = 'Image' // Need to pass which type element can be draggable

const File = ({ data, index, hasWarning, checkDependencies }) => {
  const ref = useRef(null)

  const [stateUpload, dispatchUpload] = useUpload()

  const [, drop] = useDrop({
    accept: type,
    hover(item) {
      if (!ref.current) return
      const newItem = item
      const dragIndex = newItem.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) return

      // Move the content
      dispatchUpload({ type: actionsUpload.MOVE_FILE, state: { dragIndex, hoverIndex } })

      // Update the index for dragged item directly to avoid flickering when half dragged
      newItem.index = hoverIndex
    },
  })

  const isDraggable = stateUpload.TOTAL_SELECT_FILES < stateUpload.MAX_SELECT_FILES

  const [{ isDragging }, drag] = useDrag({
    item: { type, id: data.id, index },
    canDrag: isDraggable,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  // initialize drag and drop into the element
  drag(drop(ref))

  const hasError = !!data.error
  const isCover = data.cover

  const Progress = () => {
    if (data.uploadStatus !== 'uploading') return null
    const value = data ? Math.round(data.percentage) : 0
    return (
      <CircularContainer>
        <CircularProgress variant="determinate" value={value} />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            fontSize="xsmall"
            component="div"
            color="textSecondary"
          >{`${value}%`}</Typography>
        </Box>
      </CircularContainer>
    )
  }

  return (
    <Grid ref={ref} item style={{ opacity: isDragging ? 0 : 1 }}>
      <Preview hasError={hasError}>
        <FileActions
          file={data}
          hasError={hasError}
          hasWarning={hasWarning}
          checkDependencies={checkDependencies}
        />
        <Thumbnail
          hasError={hasError}
          src={data.src ? data.src : data.url}
          state={data.uploadStatus}
          alt={data.id}
        />
        <Progress state={data.uploadStatus} />
        {isCover && <FirstMsg> {i18n.t('product.productUpload.imageDefault')}</FirstMsg>}
        {hasError && <FileError file={data} hasError={hasError} />}
      </Preview>
    </Grid>
  )
}

File.propTypes = {
  index: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  hasWarning: PropTypes.bool,
  checkDependencies: PropTypes.bool,
}

File.defaultProps = {
  hasWarning: false,
  checkDependencies: false,
}

export default File
